/**
 * Reports → Collections (spec §2.4 P5, D-INV-13). Money paid against invoices
 * in a period, and the period's credit sales (POS sales on account).
 *
 * Money once: credit sales are already in the sales report on their order
 * date; here they are only split out. Collections are not sales, and paid
 * sales sent as invoice-receipts are never collections.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import InvoiceRepository, { CollectionsResult } from '../../../internals/repository/InvoiceRepository';
import { ErrorBanner } from '../../invoice/components/VoidModal';
import { errorText } from '../../invoice/detailModel';
import { date, money } from '../../invoice/format';
import { breakdown, METHOD_LABEL, SOURCE_LABEL } from '../../invoice/posModel';
import { useSelectedShop } from '../../invoice/useInvoicing';

const capsCls = 'text-[11px] font-semibold uppercase tracking-[.07em] text-text3';
const GRID = '110px minmax(120px,1fr) minmax(140px,1.2fr) 120px 140px 130px';

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const monthStart = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth(), 1)); };

export default function CollectionsReportView() {
  const shop = useSelectedShop();
  const shopId: string | undefined = shop?.shopId;
  const cur = shop?.currency || 'LKR';
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(() => iso(new Date()));
  const [data, setData] = useState<CollectionsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!shopId) return;
    setError(null);
    setData(null);
    try {
      setData(await InvoiceRepository.collections(shopId, { from, to }));
    } catch (e) {
      setError(errorText(e));
    }
  }, [shopId, from, to]);

  useEffect(() => { load(); }, [load]);

  const c = data?.collections;
  const cs = data?.creditSales;
  const cards = [
    { label: 'Collected', value: c ? money(c.totalMinor, cur) : '—', sub: c ? `${c.count} ${c.count === 1 ? 'payment' : 'payments'}` : '' },
    { label: 'At the counter', value: c ? money(c.bySource?.POS ?? 0, cur) : '—', sub: 'Cash or card at the POS' },
    { label: 'Credit sales', value: cs ? money(cs.totalMinor, cur) : '—', sub: cs ? `${cs.count} on account · already in sales` : '' },
    { label: 'Credit sales unpaid', value: cs ? money(cs.outstandingMinor, cur) : '—', sub: 'Still owed today' },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-text2">
          From
          <input type="date" className="nl-input" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-text2">
          To
          <input type="date" className="nl-input" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} />
        </label>
      </div>

      <ErrorBanner text={error} />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {cards.map((k) => (
          <div key={k.label} className="nl-card flex flex-col gap-1 px-4 py-3.5">
            <span className={capsCls}>{k.label}</span>
            <span className="nl-num text-[20px] font-semibold tabular-nums text-text1">{k.value}</span>
            <span className="text-[12px] text-text3">{k.sub}</span>
          </div>
        ))}
      </div>

      {c && c.count > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {[['By method', breakdown(c.byMethod, METHOD_LABEL)], ['By source', breakdown(c.bySource, SOURCE_LABEL)]].map(([title, rows]: any) => (
            <div key={title} className="nl-card flex flex-col gap-2 px-4 py-3.5">
              <span className={capsCls}>{title}</span>
              {rows.map((r: any) => (
                <div key={r.key} className="flex justify-between text-[14px]">
                  <span className="text-text2">{r.label}</span>
                  <span className="nl-num tabular-nums text-text1">{money(r.amountMinor, cur)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="nl-card overflow-x-auto p-0">
        <div className="min-w-[760px]" role="table" aria-label="Invoice collections">
          <div role="row" className={`grid items-center gap-3 bg-surface2 px-[18px] py-2.5 ${capsCls}`} style={{ gridTemplateColumns: GRID }}>
            <span role="columnheader">Paid on</span>
            <span role="columnheader">Invoice</span>
            <span role="columnheader">Customer</span>
            <span role="columnheader">Method</span>
            <span role="columnheader">Source</span>
            <span role="columnheader" className="text-right">Amount</span>
          </div>
          {!data && !error && (
            <div className="border-t border-borderc px-[18px] py-5" aria-hidden>
              <span className="block h-2.5 w-1/2 animate-pulse rounded bg-surface3" />
            </div>
          )}
          {c && c.items.length === 0 && (
            <div className="border-t border-borderc px-5 py-10 text-center text-text2">No invoice payments in this period.</div>
          )}
          {c && c.items.map((p) => (
            <div key={p.paymentId} role="row" className="grid items-center gap-3 border-t border-borderc px-[18px] py-[11px] text-[14px]" style={{ gridTemplateColumns: GRID }}>
              <span role="cell" className="text-text2">{date(p.paidOn)}</span>
              <span role="cell" className="truncate font-semibold">
                <Link to={`/invoices/${encodeURIComponent(p.docId)}`} className="text-text1 hover:text-brand">{p.number ?? '—'}</Link>
              </span>
              <span role="cell" className="truncate text-text1">{p.customerName ?? '—'}</span>
              <span role="cell" className="text-text2">{METHOD_LABEL[p.method] ?? p.method}</span>
              <span role="cell" className="text-text2">{SOURCE_LABEL[p.source] ?? p.source}</span>
              <span role="cell" className="nl-num text-right font-semibold tabular-nums">{money(p.amountMinor, cur)}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="m-0 text-[12px] text-text3">
        Credit sales are POS sales on account. They already count in Sales Summary and Revenue on the day of the sale; they are shown here only so you can see how much is still owed.
      </p>
    </div>
  );
}
